//TO:DO :- Implement generics to fix type casting for response

import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { CellProps } from 'react-table';
import { toast } from 'react-toastify';
import {
  SendReviewInvitationApiPayload,
  activateMultipleResidents,
  activateResident,
  createResident,
  deactivateMultipleResidents,
  deactivateResident,
  getResidents,
  sendReviewInvitation,
  sendWelcomeMail,
  removeFromGoogleReviewOutreach,
  verifyResidentPhone,
  restrictResident,
  unrestrictResident,
  RestrictResidentPayload
} from '~/api/resident';
import Checkbox from '~/components/CheckBox/CheckBox';
import Label from '~/components/Label';
import { VENDOR_NAME_KEY } from '~/theme/vendorConstants';
import {
  BUILDING_USER_LABEL,
  BUILDING_USER_LABEL_STYLE,
  isBuildingUserEmail
} from '~/helpers/buildingUser';
import { useQueryClient } from 'react-query';
import { buildingQueryKey, fetchBuilding } from '~/hooks/building/useBuilding';
import CobuConstants from '~/helpers/cobuConstants';
import {
  CreateResidentBodyType,
  ResidentUIModel
} from '~/types/residentManagement';
import residentManagement, {
  loader,
  setResidents,
  toggleActive,
  resetState,
  updateSelection,
  initialState,
  hideLoader,
  toggleUserCreateModel,
  setShowGatheringTemplates
} from '~/reducers/residentManagement';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '~/redux/store';
import { Roles } from '~/enums/Roles';
import { fetchBuildingDashboardConfig, updateDashboardConfig, fetchBuildingTier } from '~/api/building';
import { emperor } from '~/services';

export default (buildingId: string) => {
  const queryClient = useQueryClient();
  const [residentState, dispatch] = useReducer(
    residentManagement,
    initialState
  );
  const history = useHistory();
  const isCreatePMUser = useRef(false);
  const [downloading, setDownloading] = useState<{ users: boolean; tenants: boolean }>({
    users: false,
    tenants: false,
  });

  const { userRole } = useSelector((state: RootState) => ({
    userRole: state.auth.userRole
  }));

  // Authoritative building tier (backend-computed) for gating the Block control.
  const [buildingTier, setBuildingTier] = useState<{
    isIntegrated: boolean;
    isPaid: boolean;
    allowPmBlocking: boolean;
  } | null>(null);

  /* Data Logics*/

  useEffect(() => {
    getBuildingResidents(false);
    loadBuildingTier();
  }, []);

  const loadBuildingTier = async () => {
    try {
      const res = await fetchBuildingTier(buildingId);
      setBuildingTier(res.data);
    } catch (error) {
      // Non-fatal: control simply won't render until tier is known.
    }
  };

  const getBuildingResidents = async (isRefresh: boolean = true) => {
    dispatch(
      loader({
        loadingString: isRefresh
          ? CobuConstants.residentManagement.refreshData
          : ''
      })
    );

    try {
      const residentsResponse = await getResidents(buildingId);
      const isVendorMode = !!sessionStorage.getItem(VENDOR_NAME_KEY);
      const building = await queryClient.fetchQuery(
        buildingQueryKey(buildingId),
        () => fetchBuilding(buildingId),
        { staleTime: 5 * 60 * 1000 }
      );
      // building.VCMCommenter/PMCommenter are emails, not uuids (set
      // server-side in get-user-buildings.js). Only exclude on
      // externally-integrated buildings, matching get-stats.js exactly -
      // a non-integrated building's PMCommenter can be a real resident.
      const commenterEmails = [building?.VCMCommenter, building?.PMCommenter].filter(Boolean);
      const shouldExcludeCommenters = isVendorMode && building?.isExternallyIntegrated;
      const residents = sanitizeResidentdata(residentsResponse.data.residents)
        .filter(
          (r: ResidentUIModel) => !(shouldExcludeCommenters && commenterEmails.includes(r.email))
        )
        .map((r: ResidentUIModel) => ({
          ...r,
          isBuildingUser: isBuildingUserEmail(r.email, building?.PMCommenter)
        }));
      const active = residents.filter((r: ResidentUIModel) => !r.deactivated);
      const deactivated = residents.filter(
        (r: ResidentUIModel) => r.deactivated
      );
      dispatch(
        setResidents({
          activeResidents: sortData(active),
          deactivatedResidents: sortData(deactivated)
        })
      );
    } catch (error) {
      if (error) {
        toast(error?.message);
      }
    }
  };

  const setupBuildingConfig = async () => {
    try {
      dispatch(
        loader({
          loadingString: ''
        })
      );
      const { data } = await fetchBuildingDashboardConfig(buildingId);
      const { showGatheringTemplates } = data.config;

      dispatch(setShowGatheringTemplates(showGatheringTemplates));
    } catch (e) {
    } finally {
      dispatch(hideLoader());
    }
  };

  const enableGatheringTemplate = async () => {
    try {
      const updatedConfig = {
        showGatheringTemplates: true,
        gatheringTemplateLevel: true,
      };
      await updateDashboardConfig(buildingId, updatedConfig);
      // refresh the building config
      await setupBuildingConfig();
    } catch (e) {
    }
  };

  const sanitizeResidentdata = (
    residents: ResidentUIModel[]
  ): ResidentUIModel[] => {
    return residents.map((resident) => {
      let intercomidSplit = resident.intercomUuid
        ? resident.intercomUuid.split('-')
        : [];
      let oneSignalidSplit = resident.playerId
        ? resident.playerId.split('-')
        : [];
      return {
        ...resident,
        fullName: resident.firstName + ' ' + resident.lastName,
        displayEmail: resident.email.substring(0, 30),
        displayIntercomId: intercomidSplit.length > 0 ? intercomidSplit[0] : '',
        displayOneSignaId:
          oneSignalidSplit.length > 0 ? oneSignalidSplit[0] : ''
      };
    });
  };

  const sortData = (items: ResidentUIModel[]): ResidentUIModel[] => {
    return items.sort((first, second) =>
      first.fullName.toLowerCase() < second.fullName.toLowerCase() ? 1 : -1
    );
  };

  const createBuildingResident = async (resident: CreateResidentBodyType) => {
    dispatch(
      loader({
        loadingString: CobuConstants.residentManagement.createResidentLoader
      })
    );

    try {
      await createResident(resident);
      if(residentState.showGatheringTemplates === false){
        // default value for showGatheringTemplates is null, so if it is false, we need to enable the gathering template
        await enableGatheringTemplate();
      }
      toast(CobuConstants.residentManagement.userCreateSuccess);
      refreshData();
    } catch (error) {
      dispatch(hideLoader());
      if (error) {
        toast(error?.message);
      }
    }
  };

  const deactivateBuildingResident = async (residentId: string) => {
    dispatch(
      loader({
        loadingString: CobuConstants.residentManagement.deactivateLoader
      })
    );

    try {
      const deactivateUserResp = await deactivateResident(residentId);
      toast(CobuConstants.residentManagement.deactivateSuccess);
      refreshData();
    } catch (error) {
      dispatch(hideLoader());
      if (error) {
        toast(error?.message);
      }
    }
  };

  const verifyResidentPhoneNumber = async (residentId: string) => {
    dispatch(
      loader({
        loadingString: CobuConstants.residentManagement.verifyPhoneLoader
      })
    );

    try {
      await verifyResidentPhone(residentId);
      toast(CobuConstants.residentManagement.verifyPhoneSuccess);
      refreshData();
    } catch (error) {
      dispatch(hideLoader());
      if (error) {
        toast(error?.message);
      }
    }
  };

  const restrictBuildingResident = async (
    residentId: string,
    payload: RestrictResidentPayload = {}
  ) => {
    dispatch(loader({ loadingString: 'Blocking user...' }));
    try {
      await restrictResident(residentId, payload);
      toast('User blocked');
      refreshData();
    } catch (error) {
      dispatch(hideLoader());
      if (error) {
        toast(error?.message);
      }
    }
  };

  const unrestrictBuildingResident = async (residentId: string) => {
    dispatch(loader({ loadingString: 'Unblocking user...' }));
    try {
      await unrestrictResident(residentId);
      toast('User unblocked');
      refreshData();
    } catch (error) {
      dispatch(hideLoader());
      if (error) {
        toast(error?.message);
      }
    }
  };

  const removeFromReviewOutreach = async (user: string) => {
    try {
      const removeFromReviewOutreachResp = await removeFromGoogleReviewOutreach(
        user
      );
    } catch (error) {
      if (error) {
      }
    }
  };

  const deactivateMultipleBuildingResident = async (residentIds: string[]) => {
    if (!residentIds.length) {
      toast(CobuConstants.residentManagement.nilSelectionError);
      return;
    }
    dispatch(
      loader({
        loadingString:
          CobuConstants.residentManagement.deactivateMultipleLoader(
            residentIds.length
          )
      })
    );

    try {
      const deactivateUserResp = await deactivateMultipleResidents(residentIds);
      toast(
        CobuConstants.residentManagement.deactivateMultipleSuccess(
          residentIds.length
        )
      );
      refreshData();
    } catch (error) {
      dispatch(hideLoader());
      if (error) {
        toast(error?.message);
      }
    }
  };

  const activateBuildingResident = async (residentId: string) => {
    dispatch(
      loader({
        loadingString: CobuConstants.residentManagement.activateLoader
      })
    );

    try {
      const deactivateUserResp = await activateResident(residentId);
      toast(CobuConstants.residentManagement.activateSuccess);
      refreshData();
    } catch (error) {
      dispatch(hideLoader());
      if (error) {
        toast(error?.message);
      }
    }
  };

  const activateMultipleBuildingResident = async (residentIds: string[]) => {
    if (!residentIds.length) {
      toast(CobuConstants.residentManagement.nilSelectionError);
      return;
    }
    dispatch(
      loader({
        loadingString: CobuConstants.residentManagement.activateMultipleLoader(
          residentIds.length
        )
      })
    );

    try {
      const deactivateUserResp = await activateMultipleResidents(residentIds);
      toast(
        CobuConstants.residentManagement.activateMultipleSuccess(
          residentIds.length
        )
      );
      refreshData();
    } catch (error) {
      dispatch(hideLoader());
      if (error) {
        toast(error?.message);
      }
    }
  };

  const welcomeMultipleBuildingResident = async (residentIds: string[]) => {
    if (!residentIds.length) {
      toast(CobuConstants.residentManagement.nilSelectionError);
      return;
    }
    dispatch(
      loader({
        loadingString:
          residentIds.length > 1
            ? CobuConstants.residentManagement.welcomeMailMultipleLoader(
              residentIds.length
            )
            : CobuConstants.residentManagement.welcomeMailLoader
      })
    );

    try {
      const deactivateUserResp = await sendWelcomeMail(residentIds);
      toast(
        residentIds.length > 1
          ? CobuConstants.residentManagement.welcomeMailMultipleSuccess(
            residentIds.length
          )
          : CobuConstants.residentManagement.welcomeMailSuccess
      );
      refreshData();
    } catch (error) {
      dispatch(hideLoader());
      if (error) {
        toast(error?.message);
      }
    }
  };

  const parseFilenameFromContentDisposition = (
    cd: string | null | undefined,
    fallback: string
  ) => {
    if (!cd) return fallback;
    const match = /filename\*=UTF-8''([^;]+)|filename=\"([^\"]+)\"/i.exec(cd);
    try {
      return decodeURIComponent((match && (match[1] || match[2])) || fallback);
    } catch (_e) {
      return fallback;
    }
  };

  const downloadCsv = async (
    kind: 'users' | 'tenants',
    buildingNameFallback?: string
  ) => {
    setDownloading((p) => ({ ...p, [kind]: true }));
    try {
      const url = `/api/buildings/${encodeURIComponent(buildingId)}/export/${kind}.csv`;
      const resp = await emperor.get(url, { responseType: 'blob' as any });
      const blob: Blob = resp.data;
      const cd = (resp.headers && (resp.headers['content-disposition'] as string)) || undefined;
      const fallback = `${buildingNameFallback || 'building'}_${kind}.csv`;
      const filename = parseFilenameFromContentDisposition(cd, fallback);

      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast(`Downloaded ${filename}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      toast('Failed to download. Please try again.');
    } finally {
      setDownloading((p) => ({ ...p, [kind]: false }));
    }
  };

  const sendReviewInvitationForResident = async (
    payload: SendReviewInvitationApiPayload
  ) => {
    try {
      const sendReviewInvitationResp = await sendReviewInvitation(payload);
      toast(sendReviewInvitationResp.data.message);
    } catch (error) {
      dispatch(hideLoader());
      if (error) {
        toast(error?.message);
      }
    }
  };

  /* UI Logics */

  const toggleActiveState = () => {
    dispatch(toggleActive());
  };

  const showHideUserCreateModal = async () => {
    if(!residentState.showCreateModal && residentState.showGatheringTemplates === null){
      // setup building config only if the modal is not open and the config is not set
      await setupBuildingConfig();
    }
    dispatch(toggleUserCreateModel());
  };

  const refreshData = () => {
    dispatch(resetState());
    getBuildingResidents();
  };

  /* Need to decouple this logic to UI hook */
  const residentTableConfig = useMemo(
    () => [
      ...(userRole !== Roles.PM && userRole !== Roles.PM_FREE ? [{
        id: 'check',
        Cell: (cell: CellProps<ResidentUIModel>) => {
          const option = {
            text: CobuConstants.empty,
            value: cell.row.original.uuid
          };
          let selectedOption = '';
          if (
            residentState.selectedIds.activeSelectedIds.includes(
              cell.row.original.uuid
            ) ||
            residentState.selectedIds.deactivatedSelectedIds.includes(
              cell.row.original.uuid
            )
          ) {
            selectedOption = cell.row.original.uuid;
          }
          return (
            <Checkbox
              index={cell.row.index}
              defaultSetting
              option={option}
              selectedOption={selectedOption}
              setSelectedOption={function (value: string): void {
                const updatedSelection = residentState.isActive
                  ? {
                    ...residentState.selectedIds,
                    activeSelectedIds:
                      residentState.selectedIds.activeSelectedIds.includes(
                        value
                      )
                        ? residentState.selectedIds.activeSelectedIds.filter(
                          (v) => v != value
                        )
                        : [
                          ...residentState.selectedIds.activeSelectedIds,
                          value
                        ]
                  }
                  : {
                    ...residentState.selectedIds,
                    deactivatedSelectedIds:
                      residentState.selectedIds.deactivatedSelectedIds.includes(
                        value
                      )
                        ? residentState.selectedIds.deactivatedSelectedIds.filter(
                          (v) => v != value
                        )
                        : [
                          ...residentState.selectedIds
                            .deactivatedSelectedIds,
                          value
                        ]
                  };
                dispatch(updateSelection(updatedSelection));
              }}
            />
          );
        }
      }] : []),
      {
        Header: 'NAME',
        accessor: 'fullName',
        Cell: (cell: CellProps<ResidentUIModel>) => {
          return (
            <div
              onClick={() =>
                history.push(
                  CobuConstants.navUrls.residentProfileLink(
                    buildingId,
                    cell.row.original.uuid
                  )
                )
              }
              className='default-text pointer'
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div>{cell.row.original.fullName}</div>
                {cell.row.original.isHomebody && !sessionStorage.getItem(VENDOR_NAME_KEY) && (
                  <Label title='HomeBody User' />
                )}
                {cell.row.original.isBuildingUser &&
                  (userRole === Roles.Vcm || userRole === Roles.Superadmin) && (
                    <Label title={BUILDING_USER_LABEL} style={BUILDING_USER_LABEL_STYLE} />
                  )}
              </div>
            </div>
          );
        }
      },
      {
        Header: 'EMAIL',
        accessor: 'displayEmail'
      },
      {
        Header: 'UNIT NUMBER',
        accessor: 'roomNumber'
      },
      {
        Header: 'PHONE',
        accessor: 'phone'
      },
      ...(userRole !== Roles.PM && userRole !== Roles.PM_FREE ? [{
        Header: 'INTERCOM',
        accessor: 'displayIntercomId',
        Cell: (cell: CellProps<ResidentUIModel>) => {
          let id = cell.row.original.displayIntercomId
            ? cell.row.original.displayIntercomId + ' \u2192'
            : '';
          return (
            <a
              href={CobuConstants.urls.intercomUrl(
                cell.row.original.intercomUuid
              )}
              target='_blank'
              className='default-text'
            >
              {id}
            </a>
          );
        }
      },
      {
        Header: 'ONESIGNAL',
        accessor: 'displayOneSignaId',
        Cell: (cell: CellProps<ResidentUIModel>) => {
          let id = cell.row.original.displayOneSignaId
            ? cell.row.original.displayOneSignaId + ' \u2192'
            : '';
          return (
            <a
              href={CobuConstants.urls.oneSignalUrl(cell.row.original.playerId)}
              target='_blank'
              className='default-text'
            >
              {id}
            </a>
          );
        }
      }] : [])
    ],
    [residentState.selectedIds, residentState.isActive]
  );

  return {
    residentState,
    residentTableConfig,
    enableGatheringTemplate,
    createBuildingResident,
    deactivateBuildingResident,
    deactivateMultipleBuildingResident,
    activateBuildingResident,
    activateMultipleBuildingResident,
    welcomeMultipleBuildingResident,
    sendReviewInvitationForResident,
    removeFromReviewOutreach,
    verifyResidentPhoneNumber,
    restrictBuildingResident,
    unrestrictBuildingResident,
    buildingTier,

    toggleActiveState,
    showHideUserCreateModal,
    isCreatePMUser,
    downloadCsv,
    downloading,
    refreshData
  };
};
